import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Raffle, RaffleDraw } from '@core/models/raffle.model';
import { Cart } from '@core/models/cart.model';
import { User } from '@core/models/user.model';
import { PaymentCard } from '@core/models/payment-card.model';
import { TemplateModule } from '@project/shared/template/template.module';
import { CartService } from '@core/services';
import { Select } from '@ngxs/store';
import { CartState, CartStateModel } from '@store/cart/cart.state';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RaffleInstantLogoComponent } from '@components/raffle-instant-badge/raffle-instant-logo.component';

@Component({
  standalone: true,
  selector: 'app-item-card',
  templateUrl: './item-card.component.html',
  styleUrls: ['./item-card.component.scss'],
  imports: [CommonModule, TemplateModule, RaffleInstantLogoComponent]
})
export class ItemCardComponent implements OnInit, OnDestroy {
  @Input() raffles: Array<Raffle> | null = [];
  @Input() cart: Cart;
  @Input() warnMaxTicket: {
    id?: {
      quantity: number;
      show: boolean;
    };
  };
  @Input() isGettingTickets: boolean | null = false;
  @Input() user: User;
  @Input() selectedPaymentCard: PaymentCard | null = null;
  @Input() monthlySpend: number;
  @Output() raffleQuantityChange = new EventEmitter();
  @Output() showTokenEditModal = new EventEmitter();
  @Output() postCartAddPopupRaffle = new EventEmitter();

  @Select(CartState.state) cartState$: Observable<CartStateModel>;
  globalCartItemsIDs: number[] = [];
  rejectOptionalRaffle: boolean = false;
  minTickets: Object | null = {};
  maxTickets: Object | null = {};
  _alive$ = new Subject();
  globalCart: Cart = null;
  rafflePopupInformation: RaffleDraw | null = null;
  minAvailablePricePopupRaffle: number = 5;
  instantWinBlocker: boolean = false;
  safePlayingBlocker: boolean = false;

  ngOnDestroy(): void {
    this._alive$.next();
    this._alive$.complete();
  }

  ngOnInit(): void {
    let raffleDraw: RaffleDraw | null = null;
    this.cartState$.pipe(takeUntil(this._alive$)).subscribe(state => {
      this.globalCart = state.cart;
      this.globalCartItemsIDs =
        state.cart.items?.map(item => Number(item.type_id1)) || [];
    });
    let rafflesIDs = this.raffles?.map(raffle => Number(raffle.id));
    this.raffles?.forEach(raffle => {
      this.minTickets[raffle.id] = raffle.min_tickets_per_user || 1;
      this.maxTickets[raffle.id] = this.maximunTickets(raffle);
      let raffleDrawNull =
        !raffleDraw &&
        raffle.popup_raffle &&
        !rafflesIDs.includes(raffle.popup_raffle.id);

      let raffleDrawSoonest =
        raffle.popup_raffle &&
        (raffleDraw
          ? Date.parse(raffle.end_at) < Date.parse(raffleDraw.end_at)
          : true) &&
        !rafflesIDs.includes(raffle.popup_raffle.id);

      if (this.globalCartItemsIDs.includes(raffle.popup_raffle?.id)) {
        raffle.popup_raffle = null;
      } else {
        if (raffleDrawNull || raffleDrawSoonest) {
          raffleDraw =  raffle.popup_raffle;
        }
      }
    });
    this.rafflePopupInformation = raffleDraw;

    this.instantWinBlocker =
      this.user.payments_block_type !== null
        ? this.user.payments_block_type == 'iw' &&
          this.rafflePopupInformation.instant
        : false;

    this.safePlayingBlocker =
      this.user.monthly_spend_limit && this.pricePopupRaffle > 0
        ? this.monthlySpend + this.pricePopupRaffle * this.quantityTicketsToBuy > this.user.monthly_spend_limit
        : false;
  }

  get hasPopupRaffle(): boolean {
    return this.rejectOptionalRaffle
      ? false
      : !this.instantWinBlocker &&
          !this.safePlayingBlocker &&
          this.raffles.some(item => item.popup_raffle);
  }

  get pricePopupRaffle(): number {
    if (this.rafflePopupInformation && this.rafflePopupInformation.offers.length > 0) {
      return Number(this.rafflePopupInformation.offers[0].price);
    }
    return 0;
  }

  get quantityTicketsToBuy(): number {
    return this.rafflePopupInformation ? this.pricePopupRaffle >= this.minAvailablePricePopupRaffle
    ? 1
    : this.pricePopupRaffle > 0
      ? Math.ceil(this.minAvailablePricePopupRaffle / this.pricePopupRaffle)
      : 1 : 0;
  }

  popupQuantityOnCart(id: number) {
    return (
      this.cart?.items.find(itemCart => Number(itemCart.type_id1) === id)
        ?.quantity || 1
    );
  }

  addPopupRaffleToCart() {
    this.postCartAddPopupRaffle.emit({
      raffleId: this.rafflePopupInformation.id,
      quantity: this.quantityTicketsToBuy
    });
    this.rejectOptionalRaffle = true;
  }

  handleShowTokenEditModal() {
    this.showTokenEditModal.emit();
  }

  checkMaxEntries(raffle: Raffle) {
    let maxEntrants, maxEntries, userQuantities;

    if (raffle.max_entrants > 0) raffle.max_entrants >= raffle.current_entrants;

    if (raffle.max_entries > 0)
      maxEntries = raffle.max_entries >= raffle.current_entries;

    return maxEntrants || maxEntries || userQuantities;
  }

  getWarn(id: number) {
    if (this.warnMaxTicket[id] !== null) {
      return this.warnMaxTicket[id];
    }
    return { show: false };
  }

  changeTickets(raffleId: number, cartItemID: string, quantity: number) {
    this.raffleQuantityChange.emit({ raffleId, cartItemID, quantity });
  }

  maximunTickets(raffle: Raffle) {
    let limitsTickets = [];
    if (raffle.ticket_limit_per_user && raffle.ticket_limit_per_user > 0)
      limitsTickets.push(raffle.ticket_limit_per_user);

    if (raffle.ticket_limit_per_user_per_day && raffle.ticket_limit_per_user_per_day > 0)
      limitsTickets.push(raffle.ticket_limit_per_user_per_day);

    if (raffle.max_entries && raffle.max_entries > 0) limitsTickets.push(raffle.max_entries);

    if (this.warnMaxTicket[raffle.id] && this.warnMaxTicket[raffle.id].quantity > 0)
      limitsTickets.push(this.warnMaxTicket[raffle.id].quantity);

    return limitsTickets.length > 0 ? Math.min(...limitsTickets) : null;
  }
}
